import {
  useEffect,
  useState,
} from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import styles from "./ScrollController.module.css";

const steps = [
  ["cover", "시작"],
  ["about", "소개"],
  ["skills", "기술"],
  ["experience", "경력"],
  ["project-overview", "프로젝트"],
  ["contact", "연락처"],
] as const;

type StepId = (typeof steps)[number][0];

/*
 * 일반 모바일:
 *   폭 820px 이하
 *
 * 폴드·플립 가로 화면:
 *   폭 920px 이하 + 높이 500px 이하
 *
 * Galaxy Z Fold 5 가로 882 × 344 포함
 */
const MOBILE_MEDIA_QUERY = [
  "(max-width: 820px)",
  [
    "(max-width: 920px)",
    "(max-height: 500px)",
    "(orientation: landscape)",
  ].join(" and "),
].join(", ");

export function ScrollController() {
  const reduceMotion = useReducedMotion();

  const [activeId, setActiveId] =
    useState<StepId>(steps[0][0]);

  const [hintId, setHintId] =
    useState<StepId | null>(
      steps[0][0],
    );

  const [
    isScrolling,
    setIsScrolling,
  ] = useState(false);

  const [progress, setProgress] =
    useState(0);

  /*
   * 사용자가 직접 컨트롤러를 축소했는지 여부
   */
  const [
    isCompact,
    setIsCompact,
  ] = useState(false);

  /*
   * 모바일 및 폴드 가로 화면 여부
   */
  const [
    isMobileLayout,
    setIsMobileLayout,
  ] = useState(() => {
    if (
      typeof window === "undefined"
    ) {
      return false;
    }

    return window
      .matchMedia(
        MOBILE_MEDIA_QUERY,
      )
      .matches;
  });

  /*
   * 모바일이면 항상 점 형태로 표시하고,
   * 데스크톱에서는 사용자 선택값을 사용한다.
   */
  const compact =
    isMobileLayout || isCompact;

  /*
   * 현재 섹션 이름 힌트 표시
   */
  useEffect(() => {
    setHintId(activeId);

    const timeoutId =
      window.setTimeout(() => {
        setHintId((current) =>
          current === activeId
            ? null
            : current,
        );
      }, 1000);

    return () => {
      window.clearTimeout(
        timeoutId,
      );
    };
  }, [activeId]);

  /*
   * 모바일·폴드 가로 화면 판별
   */
  useEffect(() => {
    const mediaQuery =
      window.matchMedia(
        MOBILE_MEDIA_QUERY,
      );

    const handleChange = (
      event:
        | MediaQueryListEvent
        | MediaQueryList,
    ) => {
      setIsMobileLayout(
        event.matches,
      );
    };

    handleChange(mediaQuery);

    mediaQuery.addEventListener(
      "change",
      handleChange,
    );

    return () => {
      mediaQuery.removeEventListener(
        "change",
        handleChange,
      );
    };
  }, []);

  /*
   * 현재 섹션 및 전체 스크롤 진행률 계산
   */
  useEffect(() => {
    let scrollEndTimeoutId:
      | number
      | undefined;

    let animationFrameId = 0;
    let scrolling = false;

    const mobileMediaQuery =
      window.matchMedia(
        MOBILE_MEDIA_QUERY,
      );

    let isMobile =
      mobileMediaQuery.matches;

    /*
     * 모바일 브라우저 주소창 변화로
     * innerHeight가 계속 바뀌는 문제 방지
     */
    let stableViewportHeight =
      window.innerHeight;

    const sections = steps
      .map(([id]) =>
        document.getElementById(id),
      )
      .filter(
        (
          section,
        ): section is HTMLElement =>
          section !== null,
      );

    const update = () => {
      const viewportHeight =
        isMobile
          ? stableViewportHeight
          : window.innerHeight;

      const marker =
        window.scrollY +
        viewportHeight * 0.46;

      let current = sections[0];

      for (
        const section of sections
      ) {
        if (
          section.offsetTop <= marker
        ) {
          current = section;
        }
      }

      if (current) {
        setActiveId(
          current.id as StepId,
        );
      }

      const scrollable =
        document.documentElement
          .scrollHeight -
        viewportHeight;

      setProgress(
        scrollable > 0
          ? Math.min(
              window.scrollY /
                scrollable,
              1,
            )
          : 0,
      );
    };

    const requestUpdate = () => {
      if (
        animationFrameId !== 0
      ) {
        return;
      }

      animationFrameId =
        window.requestAnimationFrame(
          () => {
            update();
            animationFrameId = 0;
          },
        );
    };

    const handleScroll = () => {
      requestUpdate();

      if (!scrolling) {
        scrolling = true;
        setIsScrolling(true);
      }

      window.clearTimeout(
        scrollEndTimeoutId,
      );

      scrollEndTimeoutId =
        window.setTimeout(() => {
          scrolling = false;
          setIsScrolling(false);
        }, 520);
    };

    const handleResize = () => {
      const nextIsMobile =
        mobileMediaQuery.matches;

      /*
       * 데스크톱에서 모바일·폴드 모드로
       * 처음 전환되는 경우 높이를 다시 저장한다.
       */
      if (
        nextIsMobile &&
        !isMobile
      ) {
        stableViewportHeight =
          window.innerHeight;
      }

      isMobile = nextIsMobile;

      if (!isMobile) {
        stableViewportHeight =
          window.innerHeight;
      }

      requestUpdate();
    };

    const handleMediaChange = (
      event: MediaQueryListEvent,
    ) => {
      isMobile = event.matches;

      stableViewportHeight =
        window.innerHeight;

      requestUpdate();
    };

    const handleOrientationChange =
      () => {
        window.setTimeout(() => {
          isMobile =
            mobileMediaQuery.matches;

          stableViewportHeight =
            window.innerHeight;

          requestUpdate();
        }, 250);
      };

    update();

    window.addEventListener(
      "scroll",
      handleScroll,
      {
        passive: true,
      },
    );

    window.addEventListener(
      "resize",
      handleResize,
    );

    window.addEventListener(
      "orientationchange",
      handleOrientationChange,
    );

    mobileMediaQuery.addEventListener(
      "change",
      handleMediaChange,
    );

    return () => {
      window.clearTimeout(
        scrollEndTimeoutId,
      );

      if (
        animationFrameId !== 0
      ) {
        window.cancelAnimationFrame(
          animationFrameId,
        );
      }

      window.removeEventListener(
        "scroll",
        handleScroll,
      );

      window.removeEventListener(
        "resize",
        handleResize,
      );

      window.removeEventListener(
        "orientationchange",
        handleOrientationChange,
      );

      mobileMediaQuery.removeEventListener(
        "change",
        handleMediaChange,
      );
    };
  }, []);

  const moveTo = (
    id: StepId,
  ) => {
    document
      .getElementById(id)
      ?.scrollIntoView({
        behavior: reduceMotion
          ? "auto"
          : "smooth",
        block: "start",
      });
  };

  return (
    <nav
      className={`
        ${styles.controller}
        ${
          hintId
            ? styles.hintVisible
            : ""
        }
        ${
          isScrolling
            ? styles.scrolling
            : ""
        }
        ${
          compact
            ? styles.compact
            : ""
        }
        no-print
      `}
      aria-label="현재 포트폴리오 단계"
    >
      <button
        type="button"
        className={
          styles.compactButton
        }
        onClick={() => {
          setIsCompact(
            (current) => !current,
          );
        }}
        aria-label={
          compact
            ? "포트폴리오 메뉴 펼치기"
            : "포트폴리오 메뉴 축소하기"
        }
        aria-expanded={!compact}
      >
        <span aria-hidden="true" />
      </button>

      <div
        className={styles.rail}
        aria-hidden="true"
      >
        <span
          style={{
            transform:
              `scaleY(${progress})`,
          }}
        />
      </div>

      <ol>
        {steps.map(
          (
            [id, label],
            index,
          ) => {
            const active =
              activeId === id;

            return (
              <li key={id}>
                {hintId === id && (
                  <span
                    className={
                      styles.hint
                    }
                    data-reduce-motion={
                      reduceMotion ||
                      undefined
                    }
                  >
                    {label}
                  </span>
                )}

                <button
                  type="button"
                  onClick={() => {
                    moveTo(id);
                  }}
                  aria-current={
                    active
                      ? "step"
                      : undefined
                  }
                  aria-label={`${
                    index + 1
                  }단계 ${label}로 이동`}
                >
                  <span
                    className={
                      styles.number
                    }
                  >
                    {String(
                      index + 1,
                    ).padStart(
                      2,
                      "0",
                    )}
                  </span>

                  <span
                    className={
                      styles.label
                    }
                  >
                    {label}
                  </span>

                  <i
                    aria-hidden="true"
                  />
                </button>
              </li>
            );
          },
        )}
      </ol>
    </nav>
  );
}